import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPostById } from '../../utils/postUtils';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useActiveComponent } from '../../Components/ActiveComponentContext.js';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import XIcon from '@mui/icons-material/X';
import LinkIcon from '@mui/icons-material/Link';
import AccordionExpand from '../../Components/Accordian';
import Tooltip from '@mui/material/Tooltip';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { categoryList } from '../../Data/pageData';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';

//A dynamic page that displays any post whose title is clicked, in its own screen
const PostDetail = ({ vertical = '450px', horizontal = '800px' }) => {
  const {
    currentUser,
    setShowAlertDialog,
    setIsBookmarkForAlert,
    bookmarks, // Provide the bookmarks state
    bookmarkCount,
    toggleBookmark, // Provide the toggleBookmark function
  } = useAuth();
  const location = useLocation();
  const { id: postID } = useParams(); // Get postID from the URL
  const { setActivePath } = useActiveComponent();
  const isBookmarked = bookmarks[postID] || false;
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // breakpoints for where screen size is decided
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  //const { images = [], title, summary } = location.state || {};
  const [currentIndex, setCurrentIndex] = useState(0); // Index for the current image
  const [postData, setPostData] = useState(null); // Store the entire post data
  // Check if state was passed via navigate
  const locationState = location.state || {};
  const [commentsCount, setCommentsCount] = useState(0);

  const navigate = useNavigate();

  const handleCommentsCountChange = (count) => {
    setCommentsCount(count);
  };

  //FUNCTIONS,
  const getCategoryIcon = (categoryName) => {
    const categoryItem = categoryList.find(
      (item) => item.name === categoryName
    );
    return categoryItem ? categoryItem.icon : null;
  };

  useEffect(() => {
    const fetchPostData = async () => {
      try {
        if (
          location.state &&
          location.state.images &&
          location.state.title &&
          location.state.summary //check if its summary or description
        ) {
          setPostData(location.state); // If state exists, use it
        } else {
          const post = await getPostById(postID); // Fetch post data by ID if no state is passed
          // console.log('POSTS ARE', post);
          if (post) setPostData(post); // Update the state with fetched data
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      }
    };
    fetchPostData();
  }, [location.state, postID]);

  //Handle navigation to user's post page, when it's clicked
  const handleUsernameClick = (path) => {
    navigate(path);
    setActivePath(path);
  };

  //used to Handle Bookmark, with isBookmarked stored in context:
  const handleBookmarkClick = () => {
    if (!currentUser) {
      setShowAlertDialog(true);
      setIsBookmarkForAlert(true);
      return;
    }
    toggleBookmark(postID, {
      creatorsUserId: postData.userId || null,
      title: postData.title,
      summary: postData.description, //check if its summary or description
      images: postData.images,
      photoURL: postData.userPhotoURL,
      username: postData.username,
      email: postData.email,
      date: postData.readableDate,
      category: postData.category,
      country: postData.location.country || null,
      //city: postData.location.city || null,
    });
  };
  const handleCategoryClick = (category) => {
    const path = generatePath(category);
    setActivePath(path); // Update the active path
    navigate(path);
  };

  const handleNextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % postData.images.length);
  };

  const handlePrevImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? postData.images.length - 1 : prevIndex - 1
    );
  };

  const handleShare = (platform) => {
    const postUrl = `${window.location.origin}/posts/${postID}`;
    let shareUrl = '';
    // const shareText = `${postData.title}\n\n${postData.summary}\n\n${postUrl}`; // Including title, summary, and URL
    const shareText = `${postData.title}\n\n${postData.description}\n\n${postUrl}`; // Including title, summary, and URL

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/share?url=${encodeURIComponent(
          postUrl
        )}&text=${encodeURIComponent(postData.title)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          postUrl
        )}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(
          title
        )}&body=${encodeURIComponent(shareText)}`;
        break;
        break;
      case 'message':
        shareUrl = `sms:?body=${encodeURIComponent(shareText)}`;

        break;
      case 'copy':
        navigator.clipboard.writeText(postUrl);
        alert('Link copied to clipboard!');
        return;
      default:
        break;
    }
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!postData) {
    return <div>Loading post details...</div>; // Show a loading state while fetching
  }
  const height = isMdUp && isLgUp ? vertical : '260px';
  const cardWidth = isMdUp && isLgUp ? horizontal : '480px';
  const maxWidth = 1900;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '100vh', // Full viewport height
          overflowY: 'auto', // Enable vertical scrolling of entire homepage
          p: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 1900,
            width: { xs: '100%', sm: '100%', md: '90%', lg: '90%', xl: '90%' },
            height: { md: '95%', lg: '95%', xl: '95%' },
            boxSizing: 'border-box',
            ml: 0,
            mr: 0,
            p: { xs: 1.5, sm: 1.5, md: 2, lg: 2.5, xl: 2.5 },
            overflowY: 'auto',
            borderRadius: 4,
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'column',
              md: 'row',
              lg: 'row',
              xl: 'row',
            },
          }}
        >
          {/* {images.length > 0 && ( */}
          {postData.images && postData.images.length > 0 ? (
            <Box position='relative'>
              <CardMedia
                component='img'
                image={postData.images[currentIndex]} // Use postData images
                alt='post image'
                sx={{
                  objectFit: 'cover', // Ensures the image covers the area
                  width: { lg: '600px', xl: '600px' },
                  height: {
                    xs: '200px',
                    sm: '200px',
                    md: '90%',
                    lg: '100%',
                    xl: '100%',
                  }, // Matches the height set earlier // Matches the height set earlier
                  borderRadius: 4,
                }}
              />
              {/* Arrows only show when there is more than one image */}
              {postData.images.length > 1 && (
                <Box>
                  <IconButton
                    disableRipple
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '10px',
                      transform: 'translateY(-50%)',
                      color: '#030202',
                      backgroundColor: 'white',
                      '&:hover': {
                        color: 'white',
                        backgroundColor: 'dimgrey',
                        boxShadow: '0px 2px 3px grey',
                      },
                    }}
                    onClick={handlePrevImage}
                  >
                    <ArrowBackIosIcon
                      sx={{
                        fontSize: { xs: 8, sm: 8, md: 12, lg: 14, xl: 14 },
                      }}
                    />
                  </IconButton>
                  <IconButton
                    disableRipple
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      color: '#030202',
                      backgroundColor: 'white',
                      '&:hover': {
                        color: 'white',
                        backgroundColor: 'dimgrey',
                        boxShadow: '0px 2px 3px grey',
                      },
                    }}
                    onClick={handleNextImage}
                  >
                    <ArrowForwardIosIcon
                      sx={{
                        fontSize: { xs: 8, sm: 8, md: 12, lg: 14, xl: 14 },
                      }}
                    />
                  </IconButton>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Typography
                variant='h6'
                sx={{
                  fontSize: { xs: 13, sm: 13, md: 14, lg: 16, xl: 16 },
                  mt: 2,
                  ml: 1,
                  mb: 0.5,
                  fontFamily: 'Montserrat',
                  fontWeight: '600',
                }}
              >
                Description
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  display: 'flex',
                  textAlign: 'flex-start',
                  width: { lg: '600px', xl: '600px' },
                  height: {
                    xs: 'auto',
                    sm: 'auto',
                    md: 'auto',
                    lg: 'auto',
                    xl: 'auto',
                  },
                  maxHeight: {
                    xs: '200px',
                    sm: '200px',
                    md: '80%',
                    lg: '80%',
                    xl: '80%',
                  },
                  overflowY: 'auto',
                  borderRadius: 4,
                  fontFamily: 'Montserrat',
                  backgroundColor: 'black',
                  color: 'white',
                  m: 0,
                  ml: 0,
                  mr: 0,
                  mt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0, xl: 0 },
                  mb: 1,
                  p: 2,
                  fontSize: { xs: 11, sm: 11, md: 13, lg: 13, xl: 13 },
                  whiteSpace: 'pre-line',
                  '&:hover': {
                    boxShadow: '0px 1px 2px inset grey',
                    cursor: 'grab',
                  },
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: 'transparent',
                  },
                  '&:hover::-webkit-scrollbar': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&:hover::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#555',
                  },
                }}
              >
                {postData.description}
              </Typography>
            </Box>
          )}

          {/* Below image, all other content */}
          <CardContent
            sx={{
              margin: { xs: 0, sm: 0, md: 1, lg: 0.5, xl: 0.5 },
              height: '90%',
              width: {
                xs: 'auto',
                sm: 'auto',
                md: '50%',
                lg: '50%',
                xl: '50%',
              },
            }}
          >
            <Box
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                gutterBottom
                variant='h6'
                //component='div'
                sx={{
                  display: 'flex',
                  textAlign: 'flex-start',
                  maxHeight: { xs: 40, sm: 40, md: 50, lg: 80, xl: 90 },
                  overflowY: 'auto',
                  fontSize: { xs: 13, sm: 13, md: 14, lg: 16, xl: 16 },
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                {postData.title}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 0,
                mb: 0,
                ml: 0,
                mr: 0,
                width: {
                  xs: '290px',
                  sm: '290px',
                  md: '300px',
                  lg: 'auto',
                  xl: 'auto',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant='body1'
                  sx={{
                    display: 'flex',
                    mt: 1,
                    mb: 1.2,
                    gap: 1,
                    fontSize: { xs: 10, sm: 10, md: 12, lg: 13, xl: 13 },
                    background: 'rgb(240,240,240)',
                    cursor: 'pointer',
                    color: '#030202',
                    borderRadius: 4,
                    fontFamily: 'Montserrat',
                    pt: 0.3,
                    pb: 0.3,
                    pl: 1.5,
                    pr: 1.5,
                    alignItems: 'flex-end',
                    '&:hover': {
                      cursor: 'pointer',
                      textShadow: '0px 0px 0.5px blue',
                    },
                  }}
                  onClick={() => handleCategoryClick(postData.category)} // Handle category click
                >
                  {/* Category: {category || postCategory} */}
                  {getCategoryIcon(postData.category)}
                  {postData.category}
                </Typography>
              </Box>

              <Tooltip title={isBookmarked ? 'Unsave Post' : 'Save Post'}>
                <Badge
                  badgeContent={bookmarkCount}
                  color='primary'
                  sx={{
                    '& .MuiBadge-badge': {
                      transform: 'translate(-50%, 20%)', // Adjust the percentage values as needed
                      fontSize: {
                        xs: '0.5rem',
                        sm: '0.5rem',
                        md: '0.5rem',
                        lg: '0.6rem',
                        xl: '0.6rem',
                      }, // Adjust font size
                      width: {
                        xs: '12px',
                        sm: '13px',
                        md: '15px',
                        lg: '17px',
                        xl: '17px',
                      }, // Adjust width
                      height: {
                        xs: '12px',
                        sm: '13px',
                        md: '15px',
                        lg: '17px',
                        xl: '17px',
                      }, // Adjust height
                      minWidth: {
                        xs: '10px',
                        sm: '13px',
                        md: '15px',
                        lg: '17px',
                        xl: '17px',
                      }, // Ensures the badge keeps its shape
                      color: 'white',
                      backgroundColor: 'crimson',
                      boxShadow: '1px 1px 1px grey',
                    },
                  }}
                >
                  <IconButton disableRipple onClick={handleBookmarkClick}>
                    {isBookmarked ? (
                      <BookmarkIcon
                        sx={{
                          fontSize: { xs: 19, sm: 22, md: 22, lg: 26, xl: 26 },
                          mt: 0,
                          color: 'crimson',
                        }}
                      />
                    ) : (
                      <BookmarkBorderIcon
                        sx={{
                          fontSize: { xs: 19, sm: 22, md: 22, lg: 26, xl: 26 },
                          mt: 0,
                          color: 'crimson',
                          '&:hover': {
                            transform: 'scale(1.11)', // Optional: adds a slight zoom effect
                            transition:
                              'transform 0.5s ease-in-out, box-shadow 0.3s ease-in-out',
                          },
                        }}
                      />
                    )}
                  </IconButton>
                </Badge>
              </Tooltip>
            </Box>

            {postData.images.length > 0 && (
              <Box>
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: 13, sm: 13, md: 14, lg: 16, xl: 16 },
                    mt: 1,
                    ml: 1,
                    mb: 0.5,
                    fontFamily: 'Montserrat',
                  }}
                >
                  Description
                </Typography>

                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    //boxShadow: '0px 1px 2px inset grey',
                    display: 'flex',
                    textAlign: 'flex-start',
                    maxHeight: { xs: 80, sm: 80, md: 120, lg: 120, xl: 120 },
                    overflowY: 'auto',
                    borderRadius: 2,
                    fontFamily: 'Montserrat',
                    m: 0,
                    ml: 0,
                    mr: 0,
                    mt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0, xl: 0 },
                    mb: 1,
                    p: 1,
                    pb: 1,
                    fontSize: { xs: 11, sm: 11, md: 13, lg: 13, xl: 13 },
                    whiteSpace: 'pre-line', // This will preserve line breaks and spaces
                    '&:hover': { boxShadow: '0px 1px 2px inset grey' },
                    '&::-webkit-scrollbar': {
                      width: '8px', // Width of the scrollbar
                      backgroundColor: 'transparent', // Hide scrollbar background by default
                    },
                    '&:hover::-webkit-scrollbar': {
                      backgroundColor: '#e0e0e0', // Show the scrollbar on hover
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888', // Show the scrollbar on hover
                      borderRadius: '10px', // Round the corners of the scrollbar thumb
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: '#555', // Darker color when hovered over the scrollbar thumb
                    },
                  }}
                >
                  {postData.description}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: 1,
                }}
              >
                <Typography
                  onClick={() => handleUsernameClick(`/${postData.username}`)}
                  variant='body1'
                  color='text.secondary'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Montserrat',
                    fontSize: { xs: 10, sm: 10, md: 10, lg: 12, xl: 12 },
                    '&:hover': {
                      cursor: 'pointer',
                      textShadow: '0px 0px 0.5px black',
                    },
                  }}
                >
                  {postData.userPhotoURL && (
                    <IconButton
                      // onClick={handleOpenUserMenu} - will send u to users page with all their posts
                      //onClick={() => handleClick(`/${postData.username}`)} //sends to dynamic path made of username
                      sx={{
                        p: 0,
                        '&:hover': {
                          p: 0,
                        },
                      }}
                    >
                      <Avatar
                        alt={postData.name || 'User'}
                        src={postData.userPhotoURL || null}
                        sx={{
                          width: { xs: 16, sm: 16, md: 18, lg: 24, xl: 24 },
                          height: { xs: 16, sm: 16, md: 18, lg: 24, xl: 24 },
                          mr: 0.5,
                          ml: 0.1,
                          mb: 0.2,
                          fontSize: { xs: 9, sm: 9, md: 10, lg: 11, xl: 11 },
                          bgcolor: postData.userPhotoURL ? 'crimson' : 'violet',
                          '&:hover': {
                            cursor: 'pointer',
                            boxShadow: '0px 0px 4px black',
                          },
                        }}
                      >
                        {!postData.userPhotoURL &&
                          (postData.name?.[0] || email?.[0] || 'U')}
                      </Avatar>
                    </IconButton>
                  )}
                  {postData.username}
                </Typography>
              </Box>
              <Typography
                variant='body1'
                color='text.secondary'
                sx={{
                  mt: 0,
                  mr: 1,
                  fontSize: { xs: 9, sm: 9, md: 10, lg: 11, xl: 11 },
                  fontFamily: 'Montserrat',
                }}
              >
                {postData.readableDate}
              </Typography>
            </Box>

            <Typography
              variant='h6'
              sx={{
                fontSize: { xs: 13, sm: 13, md: 14, lg: 16, xl: 16 },
                mt: 1,
                ml: 1,
                fontFamily: 'Montserrat',
              }}
            >
              Share
            </Typography>

            {/* Icons for sending links and bookmark */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0,
                mb: 0,
                ml: 0,
                mr: 0,
                pr: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => handleShare('facebook')}>
                  <FacebookIcon
                    sx={{
                      color: 'blue',
                      fontSize: { xs: 20, sm: 20, md: 18, lg: 24, xl: 26 },
                    }}
                  />
                </IconButton>
                <IconButton onClick={() => handleShare('twitter')}>
                  <XIcon
                    sx={{
                      color: 'white',
                      backgroundColor: 'black',
                      fontSize: { xs: 14, sm: 13, md: 13, lg: 17, xl: 19 },
                    }}
                  />
                </IconButton>
                <IconButton onClick={() => handleShare('email')}>
                  <EmailIcon
                    sx={{
                      color: 'coral',
                      fontSize: { xs: 20, sm: 17, md: 17, lg: 23, xl: 25 },
                    }}
                  />
                </IconButton>
                <IconButton onClick={() => handleShare('message')}>
                  <MessageIcon
                    sx={{
                      color: 'green',
                      fontSize: { xs: 18, sm: 15, md: 15, lg: 21, xl: 23 },
                    }}
                  />
                </IconButton>
                <IconButton onClick={() => handleShare('copy')}>
                  <LinkIcon
                    sx={{
                      color: 'indigo',
                      fontSize: { xs: 21, sm: 20, md: 20, lg: 23, xl: 23 },
                      transform: 'rotate(-45deg)',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
            <Typography
              variant='h6'
              sx={{
                fontSize: { xs: 13, sm: 13, md: 14, lg: 16, xl: 16 },
                mt: 0,
                ml: 1,
                fontFamily: 'Montserrat',
              }}
            >
              Comments ({commentsCount})
            </Typography>

            <AccordionExpand
              postId={postID}
              onCommentsCountChange={handleCommentsCountChange}
            />
          </CardContent>
        </Card>
        {/* </Box> */}
      </Box>
    </>
  );
};

export default PostDetail;
